import api from './api'

export interface QBOVendor {
  id: string
  display_name: string
  company_name: string | null
  active: boolean
}

export interface QBOLocation {
  id: string
  name: string
  active: boolean
}

export interface QBOCategory {
  id: string
  name: string
  account_type: string | null
  active: boolean
}

export interface QBOTerm {
  id: string
  name: string
  due_days: number | null
  active: boolean
}

export interface QBOTokenStatus {
  valid: boolean
  expires_at: string | null
  days_until_expiry: number | null
  realm_id: string | null
  error?: string | null
}

export async function getVendors(refresh = false): Promise<QBOVendor[]> {
  const response = await api.get<{ vendors: QBOVendor[]; total: number }>(
    '/qbo/vendors',
    { params: { refresh } }
  )
  return response.data.vendors
}

let qboListsSharedPromise: Promise<{
  categories: QBOCategory[]
  locations: QBOLocation[]
  vendors: QBOVendor[]
}> | null = null

/** Drop cached QBO list bundle (e.g. after creating a vendor server-side). */
export function invalidateQboListsCache(): void {
  qboListsSharedPromise = null
}

/**
 * Fetches categories, locations, and vendors once and shares the in-flight promise
 * across callers (e.g. multiple DocumentPanel mounts / React Strict Mode).
 */
export async function getQboListsShared(refresh = false): Promise<{
  categories: QBOCategory[]
  locations: QBOLocation[]
  vendors: QBOVendor[]
}> {
  if (refresh) {
    qboListsSharedPromise = null
  }
  if (!qboListsSharedPromise) {
    qboListsSharedPromise = Promise.all([
      getCategories(refresh),
      getLocations(refresh),
      getVendors(refresh),
    ]).then(([categories, locations, vendors]) => ({
      categories,
      locations,
      vendors,
    }))
  }
  return qboListsSharedPromise
}

export async function createVendor(displayName: string): Promise<QBOVendor> {
  const response = await api.post<QBOVendor>(
    '/qbo/vendors',
    { display_name: displayName },
    { timeout: 45000 },
  )
  invalidateQboListsCache()
  return response.data
}

export async function getLocations(refresh = false): Promise<QBOLocation[]> {
  const response = await api.get<{ locations: QBOLocation[]; total: number }>(
    '/qbo/locations',
    { params: { refresh } }
  )
  return response.data.locations
}

export async function getCategories(refresh = false): Promise<QBOCategory[]> {
  const response = await api.get<{ categories: QBOCategory[]; total: number }>(
    '/qbo/categories',
    { params: { refresh } }
  )
  return response.data.categories
}

export async function getPaymentTerms(refresh = false): Promise<QBOTerm[]> {
  const response = await api.get<{ terms: QBOTerm[]; total: number }>('/qbo/terms', {
    params: { refresh },
  })
  return response.data.terms
}

/** YYYY-MM-DD calendar due date from QBO term + issue date, or null. */
export async function getTermDueDatePreview(
  termId: string,
  issueDate: string,
): Promise<string | null> {
  const response = await api.get<{ due_date: string | null }>(
    `/qbo/terms/${encodeURIComponent(termId)}/due-date`,
    { params: { issue_date: issueDate } },
  )
  return response.data.due_date ?? null
}

export async function getTokenStatus(): Promise<QBOTokenStatus> {
  const response = await api.get<QBOTokenStatus>('/qbo/token-status')
  return response.data
}

export async function getQBOTokenStatus(): Promise<QBOTokenStatus> {
  return getTokenStatus()
}

export async function disconnectQBO(): Promise<{ success: boolean; message: string }> {
  const response = await api.post<{ success: boolean; message: string }>('/qbo/disconnect')
  return response.data
}

export async function syncCache(): Promise<{
  success: boolean
  synced: { vendors: number; locations: number; categories: number }
}> {
  const response = await api.post('/qbo/sync-cache')
  return response.data
}

export async function refreshModelSheet(): Promise<{
  success: boolean
  message: string
  completed_at?: string | null
}> {
  const response = await api.post('/qbo/refresh-model-sheet', null, {
    timeout: 360_000,
  })
  return response.data
}

/** Refresh in-memory + localStorage QBO list caches used by accounting tools. */
export async function refreshQboStoreLists(refresh = true): Promise<void> {
  const { useQBOStore } = await import('@/store/qboStore')
  const { setVendors, setLocations, setCategories, setLoading } = useQBOStore.getState()
  setLoading(true)
  try {
    const lists = await getQboListsShared(refresh)
    const syncedAt = new Date()
    setVendors(
      lists.vendors.map((v) => ({
        id: v.id,
        display_name: v.display_name,
        company_name: v.company_name,
        active: v.active,
      })),
      syncedAt
    )
    setLocations(
      lists.locations.map((l) => ({
        id: l.id,
        name: l.name,
        active: l.active,
      })),
      syncedAt
    )
    setCategories(
      lists.categories.map((c) => ({
        id: c.id,
        name: c.name,
        account_type: c.account_type,
        active: c.active,
      })),
      syncedAt
    )
  } finally {
    setLoading(false)
  }
}

export interface CategorySyncStats {
  full_resync?: boolean
  bills_processed: number
  bills_skipped: number
  credits_processed: number
  credits_skipped: number
  line_items_recorded: number
  errors: string[]
}

export async function syncCategoryHistory(
  days: number = 30,
  options?: { fullResync?: boolean }
): Promise<{
  success: boolean
  message: string
  details: CategorySyncStats
}> {
  const params: Record<string, string | number | boolean> = { days }
  if (options?.fullResync) {
    params.full_resync = true
  }
  const response = await api.post('/qbo/sync-category-history', null, {
    params,
  })
  return response.data
}
